import { NgFor, NgIf } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatCard } from '@angular/material/card';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader } from '@angular/material/expansion';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FormatNumberPipe } from '@app/shared/pipes/formatnumber.pipe';
import { SchadenUebersicht } from '@gsm/entities';
import { MomentModule } from 'ngx-moment';
import { WINDOW } from 'ngx-window-token';
import { FormDefinitionModel } from '../../dynamic-form-components/shared-models/form-definition.model';
import { FormComponent } from '../form/form.component';
import { FormChange, FormModelData } from '../form/form.model';
import { UploadProgressComponent } from '../upload-progress/upload-progress.component';
import { FormNachbringenAktinformationComponent } from './form-nachbringen-aktinformation/form-nachbringen-aktinformation.component';

@Component({
  selector:    'gsm-form-nachbringen',
  templateUrl: './form-nachbringen.component.html',
  styleUrls:   ['./form-nachbringen.component.scss'],
  standalone:  true,
  imports:     [
    MatCard,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    NgIf,
    FormNachbringenAktinformationComponent,
    NgFor,
    FormComponent,
    UploadProgressComponent,
    MatButton,
    MatProgressSpinner,
    MomentModule,
    FormatNumberPipe
  ]
})
export class FormNachbringenComponent implements OnChanges {
  private window = inject<Window>(WINDOW);

  @Input() formDefinitionModels: FormDefinitionModel[];
  @Input() isLoading = false;
  @Input() isUploading = false;
  @Input() uploadBytesLoaded = 0;
  @Input() uploadBytesTotal = 0;
  @Input() aktInfo: SchadenUebersicht;
  @Input() cockpitUrl: string;

  @Output() submitData = new EventEmitter<FormDefinitionModel<FormModelData>[]>();
  @Output() backToUebersichtEvent = new EventEmitter();
  @Output() customEvent = new EventEmitter<CustomEvent>();
  @Output() cancelUpload = new EventEmitter();
  @Output() formChange = new EventEmitter<FormChange>();

  formDefinitionData: { [layoutId: string]: FormModelData[] } = {};
  formInvalid: Record<string, boolean> = {};
  invalid = true;
  touched = false;
  showUploadProgress = false;

  private _isUploadingTimerId = 0;

  ngOnChanges({
                formDefinitionModels,
                isUploading
              }: SimpleChanges) {
    if (formDefinitionModels?.currentValue) {
      if (typeof this.formDefinitionModels === 'string') {
        this.formDefinitionModels = JSON.parse(this.formDefinitionModels);
      }
    }

    if (isUploading) {
      // if isUploading changed from false to true
      if (isUploading.currentValue && !isUploading.previousValue) {
        // wait 2 seconds before showing upload progress
        this._isUploadingTimerId = this.window.setTimeout(() => (this.showUploadProgress = true), 2000);
      } else if (!isUploading.currentValue) {
        this.showUploadProgress = false;
        clearTimeout(this._isUploadingTimerId);
      }
    }
  }

  // Following should automatically be called by each form after it is initialized.
  // Thus, a complete record of data and validity is always kept.
  onFormChange(formChange: FormChange, layoutId: string) {
    this.formDefinitionData[layoutId] = formChange.data;
    this.formInvalid[layoutId] = formChange.formInvalid;
    this.touched = true;
    this.invalid = Object.values(this.formInvalid)
      .includes(true);
    this.formChange.emit({
      ...formChange,
      taskId: layoutId
    });
  }

  onSubmit() {
    const data = this.formDefinitionModels.map(formDefinitionModel => ({
      ...formDefinitionModel,
      data: this.formDefinitionData[formDefinitionModel.layoutId]
    }));
    this.submitData.emit(data);
    this.touched = false;
  }

  onBackToUebersicht() {
    this.backToUebersichtEvent.emit();
  }
}
